import type { LucideIcon } from 'lucide-react';
import { Card, Skeleton, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { Sparkline } from './Sparkline.js';

/**
 * Ko'rsatkichning TUSI — ma'no bo'yicha, bezak uchun emas.
 *
 * Kirim yashil, chiqim ko'k, kutayotgan ish sariq, chegaradan tushgani
 * qizil. Rang ma'lumotning O'ZI: rahbar raqamni o'qimasdan turib, qaysi
 * kartochkaga qarash kerakligini biladi.
 */
export type MetricTone = 'accent' | 'success' | 'warn' | 'danger' | 'info' | 'neutral';

const CHIP: Record<MetricTone, string> = {
  neutral: 'bg-surface-2 text-text-faint',
  accent: 'bg-accent-soft text-accent',
  success: 'bg-success-soft text-success',
  warn: 'bg-warn-soft text-warn',
  danger: 'bg-danger-soft text-danger',
  info: 'bg-info-soft text-info',
};

/** Grafik chizig'ining rangi — belgichaning tusi bilan bir xil. */
const STROKE: Record<MetricTone, string> = {
  neutral: 'var(--color-text-faint)',
  accent: 'var(--color-accent)',
  success: 'var(--color-success)',
  warn: 'var(--color-warn)',
  danger: 'var(--color-danger)',
  info: 'var(--color-info)',
};

export interface MetricCardProps {
  icon: LucideIcon;
  title: string;
  hint: string;
  tone?: MetricTone;
  loading?: boolean;
  /**
   * Ko'rsatkich qiymati. `null` — manba javob bermadi. Soxta raqam
   * chizilmaydi: ombor xodimi bu raqamga qarab yuk ko'taradi, nol esa
   * "harakat bo'lmadi" deb o'qilardi.
   */
  value?: string | null;
  /**
   * Kartochka ostidagi kichik grafik — davr ichidagi shakl.
   *
   * Yolg'iz jami "100" degani "bir kunda keldimi yoki oyga yoyilganmi?"
   * degan savolga javob bermaydi. Chiziq shu savolga joy egallamasdan
   * javob beradi.
   */
  spark?: readonly number[];
}

export const MetricCard = ({
  icon: Icon,
  title,
  hint,
  tone = 'neutral',
  loading = false,
  value = null,
  spark,
}: MetricCardProps) => (
  <Card className="min-w-0">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-text-muted">{title}</p>
        <p className="mt-0.5 truncate text-xs text-text-faint">{hint}</p>
      </div>
      <span
        aria-hidden
        className={cn(
          'flex size-9 shrink-0 items-center justify-center',
          'rounded-[var(--radius-pill)]',
          CHIP[tone],
        )}
      >
        <Icon size={17} />
      </span>
    </div>

    <div className="mt-4 flex items-end justify-between gap-3">
      {loading ? (
        <Skeleton className="h-9 w-24" />
      ) : value === null ? (
        <div className="flex flex-col gap-1">
          <span aria-hidden className="tabular text-3xl leading-none font-semibold text-text-faint">
            —
          </span>
          <span className="text-xs text-text-faint">{t('dashboard.metricPending')}</span>
        </div>
      ) : (
        <span className="tabular text-3xl leading-none font-semibold tracking-tight text-text">
          {value}
        </span>
      )}

      {!loading && value !== null && spark !== undefined && spark.length > 1 ? (
        <Sparkline values={spark} stroke={STROKE[tone]} />
      ) : null}
    </div>
  </Card>
);
